import React, {useState} from "react";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import Filters from "./filters/Filters";
import ResponsiveDataTable from "./responsiveDataTable/ResponsiveDataTable";
import ActionButtons from "./actionButtons/ActionButtons";
import {useObserver} from "mobx-react-lite";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";

const PAGE_SIZE = 10;
export const CourierEquipments = () => {
    const stores = useStore();
    const [recall, setRecall] = React.useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState(
        stores.meStore.checkRole("defaultCourierEquipmentFiltersForEquipmentManager")
            ? {
                  activationStep: "candidate",
                  courierActivity: "disabled",
                  contractStatus: "pendingApproval",
                  assignmentStatus: "pending"
              }
            : stores.meStore.checkRole("defaultCourierEquipmentFiltersForOperation")
            ? {
                  activationStep: "candidate",
                  courierActivity: "disabled",
                  contractStatus: "pending",
                  assignmentStatus: "pending"
              }
            : {}
    );
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const buildQueryParams = () => {
        const params = {
            limit: PAGE_SIZE,
            page: currentPage
        };
        if (filters.zoneId) params.zoneId = filters.zoneId;
        if (filters.preferredCityId) params.preferredCityId = filters.preferredCityId;
        if (filters.contractStatus) params.contractStatus = filters.contractStatus;
        if (filters.assignmentStatus) params.assignmentStatus = filters.assignmentStatus;
        if (filters.searchText) params.searchText = filters.searchText;
        if (filters.courierActivity) params.courierActivity = filters.courierActivity;
        if (filters.activationStep) params.activationStep = filters.activationStep;
        if (filters.handoveredStartDate) params.handoveredStartDate = filters.handoveredStartDate;
        if (filters.handoveredEndDate) params.handoveredEndDate = filters.handoveredEndDate;
        if (filters.employmentType) params.employmentType = filters.employmentType;
        return params;
    };

    const [loading, courierEquipments, setCourierEquipments] = useFetch({
        fetchFn: async () => stores.equipmentStore.listCourierEquipments(buildQueryParams()),
        dependencies: [recall, filters, currentPage]
    });

    const handleRowSelection = selectedRowKeys => {
        setSelectedRowKeys(selectedRowKeys);
        const selectedRows = courierEquipments?.data?.rows?.filter(row =>
            selectedRowKeys.includes(row._id)
        );

        setSelectedRows(prev => {
            const next = [];
            [...prev, ...selectedRows]
                ?.filter(row => selectedRowKeys.includes(row?._id?.toString()))
                ?.forEach(row => {
                    if (!next.some(p => p?._id?.toString() === row?._id?.toString())) {
                        next.push(row);
                    }
                });

            return next;
        });
    };

    const handleCourierRemove = courierId => {
        setSelectedRowKeys(prev => prev.filter(id => id !== courierId));
        setSelectedRows(prev => prev.filter(row => row._id !== courierId));
    };

    const onRowsUpdated = ({updatedRecords}) => {
        setSelectedRows([]);
        setSelectedRowKeys([]);
        setCourierEquipments(prev => {
            const rows = prev?.data?.rows?.map(row => {
                const updatedRow = updatedRecords?.find(
                    courier => courier?._id?.toString() === row?._id?.toString()
                );
                return updatedRow ? updatedRow : row;
            });
            return {...prev, data: {...prev?.data, rows}};
        });
    };

    return useObserver(() => (
        <VFlex vertical gap={24} wrap="wrap">
            <Filters setCurrentPage={setCurrentPage} setFilters={setFilters} filters={filters} />
            <VFlex justify="flex-end" wrap="wrap" gap={12}>
                <ActionButtons
                    recall={() => setRecall(prev => prev + 1)}
                    selectedCouriers={selectedRows || []}
                    onCourierRemove={handleCourierRemove}
                    filters={filters}
                    onEquipmentAssigned={onRowsUpdated}
                />
            </VFlex>
            <VFlex wrap="nowrap">
                <ResponsiveDataTable
                    loading={loading}
                    data={(() => {
                        const listData = courierEquipments?.data?.rows;
                        return Array.isArray(listData) ? listData : [];
                    })()}
                    dataCount={courierEquipments?.data?.total || 0}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    pageSize={PAGE_SIZE}
                    recall={() => setRecall(prev => prev + 1)}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChange={selectedRowKeys => {
                        handleRowSelection(selectedRowKeys);
                    }}
                    onEquipmentAssigned={onRowsUpdated}
                />
            </VFlex>
        </VFlex>
    ));
};
